import { ArrowLeftIcon, WarningIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';

import { useConfig } from '@entities/config/api';
import { blockedReasonKey, useNegotiationPreview } from '@entities/negotiation';
import { useSearchSession } from '@entities/search-session';
import { isLive, useSelectVehicle, useSelection } from '@entities/selection';
import { KarlDialogue } from '@features/chat';
import { BargainingSetup, type FeeTerms } from '@features/negotiation';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { ButtonLink, Skeleton, StateBlock } from '@shared/ui';

import styles from './MandatePage.module.css';

/**
 * Маршрут мандата `/app/searches/:searchId/mandate/:vehicleId` (FE-171, FE-460).
 *
 * **С 12.09.2026 опросник перед торгом живёт на карточке переписки** — там же,
 * где потом идёт лента торга. Маршрут остаётся ради старых ссылок и ради
 * единственного случая, когда переписки по автомобилю ещё нет (контур
 * переговоров был выключен, когда подтверждали четвёрку): тогда опросник
 * показывается здесь, а выбор создаёт переписку сам.
 *
 * `?negotiation=<id>` — прежний режим новой версии мандата: теперь это
 * встречное предложение на карточке переписки, и экран ведёт туда.
 *
 * Красная кнопка на экране одна — отправка мандата на последнем шаге
 * опросника (DESIGN_SYSTEM §5.1); «Weiter» — синий контур, «Zurück» и
 * «Abbrechen» — вторичные. Отправка необратима и идёт в два шага: отметки
 * согласия, затем кнопка, без оптимистичного UI.
 */
export function MandatePage() {
  const { t } = useTranslation();
  const { searchId = '', vehicleId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const revisionId = searchParams.get('negotiation');

  const sessionQuery = useSearchSession(searchId || null);
  const selectionQuery = useSelection(searchId || null);
  const previewQuery = useNegotiationPreview(vehicleId || null);
  const { data: config } = useConfig();
  const selectVehicle = useSelectVehicle(searchId);

  const session = sessionQuery.data ?? null;
  const selection = selectionQuery.data ?? null;
  const preview = previewQuery.data ?? null;

  // Рельс Karl с диалогом заказа — как на остальных экранах воронки
  // (CLAUDE.md §8, FE-581).
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: { extra: <KarlDialogue searchId={searchId} /> },
  });

  if (revisionId !== null) {
    return <Navigate to={ROUTES.negotiation(revisionId)} replace />;
  }

  if (selectionQuery.isPending || previewQuery.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="50%" />
        <Skeleton height="24rem" />
      </div>
    );
  }

  if (selection === null || preview === null) {
    const error = selectionQuery.error ?? previewQuery.error;
    const unpaid = error instanceof ApiError && error.status === 402;
    return (
      <div className={styles.page}>
        <StateBlock
          tone={unpaid ? 'neutral' : 'error'}
          title={unpaid ? t('selection.locked.title') : t('selection.error.title')}
          description={unpaid ? t('selection.locked.text') : t('selection.error.text')}
          action={
            <ButtonLink to={ROUTES.searchTop4(searchId)} variant="secondary">
              {t('negotiation.outreach.backToTop4')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  const slot = selection.slots.filter(isLive).find((item) => item.vehicle.vehicle_id === vehicleId);

  if (slot === undefined) {
    return (
      <div className={styles.page}>
        <StateBlock
          title={t('negotiation.mandate.notInSelectionTitle')}
          description={t('negotiation.mandate.notInSelectionText')}
          action={
            <ButtonLink to={ROUTES.searchTop4(searchId)} variant="secondary">
              {t('negotiation.outreach.backToTop4')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  // Переписка есть — опросник и торг живут на её карточке.
  if (slot.negotiation_id !== null) {
    return <Navigate to={ROUTES.negotiation(slot.negotiation_id)} replace />;
  }

  const fee: FeeTerms | null =
    config !== undefined && config.success_fee.enabled
      ? { rate_bps: config.success_fee.rate_bps, flat_cents: config.success_fee.flat_cents }
      : null;

  return (
    <div className={styles.page}>
      <Link to={ROUTES.searchTop4(searchId)} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('negotiation.outreach.backToTop4')}
      </Link>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('negotiation.mandate.title')}</h1>
        <p className={styles.subtitle}>
          {slot.vehicle.make} {slot.vehicle.model}
          {slot.vehicle.variant === null ? null : ` · ${slot.vehicle.variant}`}
          {' · '}
          {slot.vehicle.seller_name ?? t('negotiation.list.unknownSeller')}
        </p>
      </header>

      {preview.can_start ? null : (
        <p className={styles.blocked} role="note">
          <WarningIcon size={16} weight="fill" aria-hidden="true" className={styles.blockedIcon} />
          <span>{t(blockedReasonKey(preview.blocked_reason ?? 'UNKNOWN'))}</span>
        </p>
      )}

      <BargainingSetup
        mode="select"
        preview={preview}
        // Переписки по автомобилю ещё нет — выбор создаёт её сам, поэтому
        // предпосылки запуска здесь и решают, доступна ли кнопка. Причина
        // названа выше, форма её не повторяет (§21.2).
        blocked={!preview.can_start}
        currency={slot.vehicle.price.currency}
        // Переписки ещё нет — нет и ответов автохауса, на которые могла бы
        // опереться цена: список открытых пунктов пуст, и опросник говорит,
        // что вопросов автохаусу ещё не задавали, а не что на всё ответили.
        findings={[]}
        checkProgress={null}
        maxQuestions={selection.max_custom_questions}
        busy={selectVehicle.isPending}
        fieldErrors={fieldErrors(selectVehicle.error, t)}
        fee={fee}
        onCancel={() => {
          void navigate(ROUTES.searchTop4(searchId));
        }}
        onSubmit={(mandate) => {
          selectVehicle.mutate(
            {
              vehicleId: slot.vehicle.vehicle_id,
              mandate,
              feeConsentHash: selection.fee_consent.text_hash,
            },
            {
              onSuccess: () => {
                void navigate(ROUTES.searchTop4(searchId));
              },
            },
          );
        }}
      />
    </div>
  );
}

/**
 * Ошибки по полям из `422 MANDATE_INVALID`.
 *
 * Код без перевода не показывается: сырой `TARGET_TOO_LOW` под полем — утечка
 * внутренностей, поэтому есть общий запасной текст.
 */
function fieldErrors(error: unknown, t: (key: string) => string): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const result: Record<string, string> = {};
  for (const item of error.fieldErrors) {
    const key = `negotiation.mandate.error.${item.code}`;
    const translated = t(key);
    result[item.field] = translated === key ? t('negotiation.mandate.error.UNKNOWN') : translated;
  }
  return result;
}
